import { prepareAIMedia, validateAIMediaUrl } from './ai-media'
import { getTelegramAiPeer } from './telegramAiPeer'

const prefix = 'dmex:telegram-ai:v1:'
const receiptRetentionMs = 86400000
const maxRetained = 2000
const receiptHashBudgetMs = 50
const validDeliveryId = (id: unknown): id is string => typeof id === 'string' && id.length > 0 && id.length <= 256 && !/[\s\u0000-\u001f\u007f]/.test(id)
const failure = (code: string, status = 'failed') => ({ ok: false, status, code })
const optional = <T>(read: () => T): T | undefined => { try { return read() } catch { return undefined } }
const isLiveReceipt = (entry: any, now: number) => entry?.state === 'sent' &&
  Number.isFinite(entry.retainUntil) && entry.retainUntil > now &&
  typeof entry.fingerprint === 'string' && /^[a-f0-9]{64}$/.test(entry.fingerprint)

/** Sending is the business operation; local receipts are only a best-effort retry cache. */
export function createTelegramAIActionHandler(options: {
  getManager: () => any; getSelfId: () => string | number;
  getUser: (peerId: string) => any | Promise<any>;
  storage?: Storage; crypto?: Crypto; prepareMedia?: typeof prepareAIMedia; timeoutMs?: number
}) {
  const inflight = new Map<string, { input: string; task: Promise<any> }>()
  const confirmed = new Map<string, { input: string; result: any; retainUntil: number }>()
  const storage = optional(() => options.storage ?? globalThis.localStorage)
  const crypt = optional(() => options.crypto ?? globalThis.crypto)
  const hash = async (value: string): Promise<string | undefined> => {
    let timer: ReturnType<typeof setTimeout>
    try {
      if (!crypt?.subtle) return undefined
      const digest = await Promise.race([crypt.subtle.digest('SHA-256', new TextEncoder().encode(value)),
        new Promise<undefined>(resolve => { timer = setTimeout(() => resolve(undefined), receiptHashBudgetMs) })])
      if (!digest) return undefined
      return Array.from(new Uint8Array(digest),
        byte => byte.toString(16).padStart(2, '0')).join('')
    } catch { return undefined } finally { clearTimeout(timer) }
  }
  const read = (key: string) => {
    try { return JSON.parse(storage?.getItem(key) ?? 'null') } catch { return null }
  }
  const remove = (key: string) => { try { storage?.removeItem(key) } catch { /* Best effort. */ } }
  const prune = (now: number) => {
    try {
      const keys: string[] = []
      for (let i = (storage?.length ?? 0) - 1; i >= 0; i--) {
        const key = storage.key(i)
        if (key?.startsWith(prefix)) keys.push(key)
      }
      const live: Array<{ key: string; retainUntil: number }> = []
      for (const key of keys) {
        const entry = read(key)
        if (!isLiveReceipt(entry, now)) {
          remove(key)
        } else live.push({ key, retainUntil: entry.retainUntil })
      }
      live.sort((a, b) => a.retainUntil - b.retainUntil)
      for (const entry of live.slice(0, Math.max(0, live.length - maxRetained + 1))) remove(entry.key)
    } catch { /* A cache failure must never become a send failure. */ }
  }
  const remember = (key: string, input: string, result: any, retainUntil = Date.now() + receiptRetentionMs) => {
    confirmed.delete(key)
    confirmed.set(key, { input, result, retainUntil })
    while (confirmed.size > maxRetained) confirmed.delete(confirmed.keys().next().value)
  }
  const save = (key: string, fingerprint: string | undefined, result: any) => {
    if (!fingerprint) return // Never persist a plaintext fallback when crypto is unavailable.
    try {
      const now = Date.now()
      prune(now)
      storage?.setItem(key, JSON.stringify({ version: 2, fingerprint, state: 'sent',
        retainUntil: now + receiptRetentionMs, platformMessageIds: result.platformMessageIds }))
    } catch { /* The platform has already sent the reply; keep its successful result. */ }
  }
  const wait = async (task: Promise<any>, timeout: any) => {
    let timer: ReturnType<typeof setTimeout>
    try { return await Promise.race([task, new Promise(resolve => {
      timer = setTimeout(() => resolve(timeout), options.timeoutMs ?? 330000)
    })]) } finally { clearTimeout(timer) }
  }
  const messageIds = (value: any) => Array.isArray(value)
    ? value.filter(id => typeof id === 'string' && /^\d+$/.test(id)).slice(0, 100) : []

  return async (data: any): Promise<any> => {
    if (!['aireply', 'aisend'].includes(data?.action)) return false
    // Snapshot primitive fields before any async lookup or media preparation.
    const p = { ...data.payload }, kind = p.msgType ?? 'text'
    if (typeof p.userId === 'number' && Number.isSafeInteger(p.userId) && p.userId > 0) p.userId = String(p.userId)
    if (typeof p.userId === 'string') p.userId = p.userId.trim()
    if (typeof p.userId !== 'string' || !p.userId || typeof p.msg !== 'string' || !p.msg.trim() ||
      !['text', 'image', 'video', 'voice'].includes(kind) ||
      (p.caption != null && typeof p.caption !== 'string')) return failure('CONTENT_INVALID')
    try { if (kind !== 'text') validateAIMediaUrl(p.msg) } catch { return failure('MEDIA_URL_INVALID') }

    const manager = options.getManager(), self = String(options.getSelfId())
    if (!/^[1-9][0-9]*$/.test(self) || !manager) return failure('ACCOUNT_NOT_READY')
    if (p.expectedSelfId !== undefined && String(p.expectedSelfId) !== self) return failure('ACCOUNT_MISMATCH')
    const eligible = async () => String(options.getSelfId()) === self &&
      !!await getTelegramAiPeer(p.userId, options) && String(options.getSelfId()) === self
    if (!await eligible() || String(options.getSelfId()) !== self) return failure('TELEGRAM_PEER_FORBIDDEN')

    const id = validDeliveryId(p.deliveryId) ? p.deliveryId : undefined
    const key = id ? prefix + self + ':' + id : undefined
    const parts = [data.action, p.userId, kind, p.msg, p.caption ?? '']
    const input = JSON.stringify(parts) // Optional deadline metadata is not message identity.
    const receipt = (value: any) => id ? { ...value, deliveryId: id, userId: p.userId, msgType: kind } : value
    const pending = key && inflight.get(key)
    if (pending) return pending.input === input
      ? wait(pending.task, receipt(failure('SEND_TIMEOUT', 'unknown'))) : receipt(failure('DELIVERY_CONFLICT'))
    const cached = key && confirmed.get(key)
    if (cached && cached.retainUntil > Date.now()) {
      return cached.input === input ? cached.result : receipt(failure('DELIVERY_CONFLICT'))
    }
    if (key) confirmed.delete(key)

    const task = (async () => {
      let prepared: Awaited<ReturnType<typeof prepareAIMedia>> | undefined
      try {
        const fingerprint = key ? await hash(input) : undefined
        const saved = key && read(key)
        if (fingerprint && isLiveReceipt(saved, Date.now())) {
          let matches = saved.fingerprint === fingerprint, comparable = true
          // Migrate old confirmed receipts whose digest also included an ISO deadline.
          if (!matches && saved.version !== 2 && Number.isFinite(saved.expires)) {
            const oldDeadline = optional(() => new Date(saved.expires).toISOString())
            const deadlines = new Set(oldDeadline ? [oldDeadline] : [])
            if (typeof p.expiresAt === 'string' && Date.parse(p.expiresAt) === saved.expires) deadlines.add(p.expiresAt)
            for (const deadline of deadlines) {
              const oldFingerprint = await hash(JSON.stringify([...parts, deadline]))
              if (!oldFingerprint) { comparable = false; break }
              if (saved.fingerprint === oldFingerprint) { matches = true; break }
            }
          }
          if (!matches && comparable) return receipt(failure('DELIVERY_CONFLICT'))
          if (matches) {
            const result = receipt({ ok: true, status: 'sent', platformMessageIds: messageIds(saved.platformMessageIds) })
            remember(key, input, result, saved.retainUntil)
            return result
          }
        }
        // Old claimed/corrupt records are not confirmation that a send occurred.
        if (kind !== 'text') prepared = await (options.prepareMedia ?? prepareAIMedia)(p.msg, kind)
        if (!await eligible() || String(options.getSelfId()) !== self) return receipt(failure('TELEGRAM_PEER_FORBIDDEN'))
        let result: any
        if (kind === 'text') {
          const completed = await manager.sendText(p.userId, p.msg)
          result = completed === undefined ? { ok: true, status: 'sent', platformMessageIds: [] } : completed
        } else if (typeof manager.sendConfirmedAIMessage === 'function') {
          const { dispose, ...media } = prepared ?? {} as any
          result = await manager.sendConfirmedAIMessage(p.userId, { msgType: kind, caption: p.caption ?? '', ...media })
        } else return receipt(failure('MEDIA_NOT_SUPPORTED'))
        if (result?.ok !== true || result.status !== 'sent') {
          return receipt(failure(result?.code || 'SEND_UNCONFIRMED', result?.status || 'unknown'))
        }
        const success = receipt({ ok: true, status: 'sent', platformMessageIds: messageIds(result.platformMessageIds) })
        if (key) {
          remember(key, input, success)
          save(key, fingerprint, success)
        }
        return success
      } catch (error: any) {
        const known = ['MEDIA_URL_INVALID', 'MEDIA_DOWNLOAD_TIMEOUT', 'MEDIA_DOWNLOAD_FAILED', 'MEDIA_TYPE_INVALID', 'MEDIA_TOO_LARGE', 'MEDIA_EMPTY',
          'VOICE_OPUS_REQUIRED', 'MEDIA_METADATA_TIMEOUT', 'MEDIA_DECODE_FAILED', 'MEDIA_METADATA_INVALID']
        return receipt(known.includes(error?.code) ? failure(error.code) : failure('SEND_UNCONFIRMED', 'unknown'))
      } finally {
        try { prepared?.dispose() } catch { /* Cleanup cannot undo a successful platform send. */ }
      }
    })()
    // Keep the actual operation until it settles, even if one caller stops waiting.
    if (key) {
      inflight.set(key, { input, task })
      task.finally(() => { inflight.delete(key) })
    }
    const result = await wait(task, receipt(failure('SEND_TIMEOUT', 'unknown')))
    return id ? result : result?.ok === true && result.status === 'sent'
  }
}
